import { FC } from 'react'
import {
  ArrowPathIcon,
  BuildingStorefrontIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PowerIcon,
} from '@heroicons/react/24/outline'

import { GroupSummary } from '@/models/operations/types'
import { classNames } from '@/utils'

type StatCardsProps = {
  summary: GroupSummary
}

type StatKey = 'total' | 'churned' | 'running' | 'failed' | 'building'

const DEPLOY_STATUS_TOOLTIP = 'Deployment status for active stores'

const stats: {
  key: StatKey
  label: string
  tooltip?: string
  icon: typeof BuildingStorefrontIcon
  valueClass: string
  iconClass: string
  iconBg: string
}[] = [
  {
    key: 'total',
    label: 'Total stores',
    icon: BuildingStorefrontIcon,
    valueClass: 'text-gray-600',
    iconClass: 'text-gray-500',
    iconBg: 'bg-gray-50',
  },
  {
    key: 'churned',
    label: 'Inactive',
    icon: PowerIcon,
    valueClass: 'text-red',
    iconClass: 'text-red',
    iconBg: 'bg-red-50',
  },
  {
    key: 'running',
    label: 'Ready',
    tooltip: DEPLOY_STATUS_TOOLTIP,
    icon: CheckCircleIcon,
    valueClass: 'text-green-500',
    iconClass: 'text-green-500',
    iconBg: 'bg-green-50',
  },
  {
    key: 'failed',
    label: 'Failed',
    tooltip: DEPLOY_STATUS_TOOLTIP,
    icon: ExclamationTriangleIcon,
    valueClass: 'text-red-200',
    iconClass: 'text-red-200',
    iconBg: 'bg-red-50',
  },
  {
    key: 'building',
    label: 'Building',
    tooltip: DEPLOY_STATUS_TOOLTIP,
    icon: ArrowPathIcon,
    valueClass: 'text-[#2E59A3]',
    iconClass: 'text-[#2E59A3]',
    iconBg: 'bg-blue-50',
  },
]

const StatCards: FC<StatCardsProps> = ({ summary }) => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
      {stats.map(({ key, label, tooltip, icon: Icon, valueClass, iconClass, iconBg }) => (
        <div
          key={key}
          title={tooltip}
          className="bg-white rounded-lg border border-gray-100 p-4 flex items-center gap-4"
        >
          <div
            className={classNames(
              'w-[50px] h-[50px] rounded-lg shrink-0 flex items-center justify-center',
              iconBg,
            )}
          >
            <Icon
              className={classNames('w-6 h-6', iconClass, {
                'animate-spin': key === 'building' && summary.building > 0,
              })}
            />
          </div>
          <div className="flex flex-col min-w-0">
            <span className={classNames('text-2xl font-bold leading-tight', valueClass)}>
              {summary[key]}
            </span>
            <span className="text-sm text-gray-500">{label}</span>
          </div>
        </div>
      ))}
    </div>
  )
}

export { StatCards }
